import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Box, Button, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import examApis from '../../apis/exams';
import DropdownSelect from '../../components/DropdownSelect';
import {
  academicYears,
  defaultAcademicYear
} from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import ConsolidatedMarksReportTable from './components/ConsolidatedMarksReportTable';
import { User } from '../../types/userType';
import { PageWrapper, PaperWrapper, Title } from '../common/styled';
import Icons from '../../components/icons';
import CustomDatePicker from '../../components/DatePicker/CustomDatePicker';
import SelectAll from '../../components/SelectAllField/SelectAllField';

interface ConsolidatedMarksReportPageProps {
  allClasses?: SchoolClassesConfig;
  school: string;
  isClassesLoading?: boolean;
  studentDetails: User[] | null;
  isLoading: boolean;
  onSearch: (academicYear: string, grade: string, division: string) => void;
  onGenerateConsolidatedReport: (
    academicYear: string,
    grade: string,
    division: string,
    terms: string[]
  ) => void;
  onDownloadConsolidatedReport: (
    academicYear: string,
    grade: string,
    division: string
  ) => void;
  onDownloadAllReportCards: (
    academicYear: string,
    reportCardDate: string
  ) => void;
  onGenerateReportCard: (
    enrollmentNo: string,
    academicYear: string,
    reportCardDate: string
  ) => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  isDownloadInProgress: boolean;
}

const ConsolidatedMarksReportPage: React.FC<
  ConsolidatedMarksReportPageProps
> = ({
  allClasses,
  school,
  isClassesLoading,
  studentDetails,
  isLoading,
  onSearch,
  onGenerateConsolidatedReport,
  onDownloadConsolidatedReport,
  onDownloadAllReportCards,
  onGenerateReportCard,
  showToast,
  isDownloadInProgress
}) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [terms, setTerms] = useState<string[]>([]);

  const [termOptions, setTermOptions] = useState<
    { name: string; id: string }[]
  >([]);
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [isTermsLoading, setIsTermsLoading] = useState(false);

  const [hasSearched, setHasSearched] = useState(false);

  const [reportCardDate, setReportCardDate] = useState(moment());

  const isFormDisabled =
    isLoading ||
    !grade ||
    !division ||
    terms.length === 0 ||
    isTermsLoading ||
    isDownloadInProgress;

  useEffect(() => {
    if (grade && division && academicYear && school) {
      setIsTermsLoading(true);
      examApis
        .getTermsFor(grade, division, academicYear, school)
        .then((response: any) => {
          const apiTerms = Array.isArray(response) ? response : [];
          setTermOptions(
            apiTerms.map((term: any) => ({ name: term.name, id: term.id }))
          );
        })
        .catch((error) => {
          console.error(error);
          showToast('Failed to load terms.', SnackbarSeverityType.error);
        })
        .finally(() => setIsTermsLoading(false));
    } else {
      setTermOptions([]);
      setTerms([]);
    }
  }, [grade, division, academicYear, school, showToast]);

  useEffect(() => {
    if (allClasses) {
      setGradeOptions(allClasses.grades || []);
    }
  }, [allClasses]);

  useEffect(() => {
    if (allClasses && grade) {
      const divisions = allClasses.classes
        .filter(
          (schoolClass: { grade: string; division: string }) =>
            schoolClass.grade === grade
        )
        .map(
          (schoolClass: { grade: string; division: string }) =>
            schoolClass.division
        );
      setDivisionOptions(divisions);
    } else if (allClasses) {
      setDivisionOptions(allClasses.divisions || []);
    }
  }, [allClasses, grade]);

  const handleSearchClick = () => {
    setHasSearched(true);
    onSearch(academicYear, grade, division);
  };

  const handleGenerateConsolidatedReportClick = () => {
    onGenerateConsolidatedReport(academicYear, grade, division, terms);
  };

  const handleDownloadConsolidatedReportClick = () => {
    onDownloadConsolidatedReport(academicYear, grade, division);
  };

  const handleDownloadAllReportCardsClick = () => {
    onDownloadAllReportCards(academicYear, reportCardDate.format('YYYY-MM-DD'));
  };

  return (
    <>
      <Title>Consolidated Marks Report</Title>
      <PaperWrapper>
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <DropdownSelect
            sx={{ minWidth: '120px !important' }}
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            value={academicYear}
            options={academicYears}
            onChange={(e) => {
              setAcademicYear(e.target.value as string);
              setGrade('');
              setDivision('');
              setTerms([]);
              setDivisionOptions([]);
              setTermOptions([]);
              setHasSearched(false);
            }}
            loading={!!isClassesLoading && academicYears.length === 0}
          />
          <DropdownSelect
            sx={{ minWidth: '85px !important' }}
            label='Grade'
            name='grade'
            placeholder='Select'
            value={grade}
            options={gradeOptions.map((g) => ({ value: g, label: g }))}
            onChange={(e) => {
              setGrade(e.target.value as string);
              setDivision('');
              setTerms([]);
              setDivisionOptions([]);
              setTermOptions([]);
              setHasSearched(false);
            }}
            loading={!!isClassesLoading && gradeOptions.length === 0}
          />
          <DropdownSelect
            sx={{ minWidth: '85px !important' }}
            label='Division'
            name='division'
            placeholder='Select'
            value={division}
            options={divisionOptions.map((d) => ({ value: d, label: d }))}
            onChange={(e) => {
              setDivision(e.target.value as string);
              setTerms([]);
              setTermOptions([]);
              setHasSearched(false);
            }}
            loading={!!isClassesLoading && divisionOptions.length === 0}
          />
          <Box sx={{ width: '170px' }}>
            <SelectAll
              label='Terms'
              placeholder='Select'
              required
              selected={terms}
              options={termOptions.map((t) => ({ value: t.id, label: t.name }))}
              onChange={(selectedTerms: string[]) => {
                setTerms(selectedTerms);
                setHasSearched(false);
              }}
              disabled={isTermsLoading}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            gap: 2,
            alignItems: 'flex-end'
          }}
        >
          <Button
            variant='contained'
            onClick={handleSearchClick}
            disabled={isFormDisabled}
          >
            <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Search'
            )}
          </Button>
          <Button
            variant='contained'
            onClick={handleGenerateConsolidatedReportClick}
            disabled={isFormDisabled}
          >
            Generate
          </Button>
          <Button
            variant='contained'
            onClick={handleDownloadConsolidatedReportClick}
            disabled={isFormDisabled}
          >
            Download
          </Button>
        </Box>
      </PaperWrapper>

      {hasSearched && academicYear && grade && division && terms.length > 0 && (
        <PageWrapper>
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              width: '100%',
              alignItems: 'center'
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <CustomDatePicker
                label='Report Card Date'
                value={reportCardDate}
                onChange={(newValue) => setReportCardDate(newValue)}
              />
            </LocalizationProvider>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              onClick={handleDownloadAllReportCardsClick}
              disabled={
                isFormDisabled || !studentDetails || studentDetails.length === 0
              }
            >
              Download All Report Cards
            </Button>
          </Box>

          <>
            <ConsolidatedMarksReportTable
              studentDetails={studentDetails || []}
              isLoading={isLoading}
              onGenerateReportCard={(studentId: string) =>
                onGenerateReportCard(
                  studentId,
                  academicYear,
                  reportCardDate.format('YYYY-MM-DD')
                )
              }
              isDownloadInProgress={isDownloadInProgress}
            />
          </>
        </PageWrapper>
      )}
    </>
  );
};

export default ConsolidatedMarksReportPage;
